import type { CaseClaimFragmentFragment } from "@alaffia-technology-solutions/client-sdk";

import type { FieldGroup } from "./ClaimDataCard.fields";

import {
  AGGREGATE_REGISTRY,
  DERIVED_REGISTRY,
  FIELD_REGISTRY,
  GROUP_LABELS,
  GROUP_ORDER,
} from "./ClaimDataCard.fields";

type Claim = NonNullable<CaseClaimFragmentFragment["claim"]>;

interface ClaimDataCardProps {
  claim: Claim | null | undefined;
}

interface SimpleRow {
  kind: "simple";
  order: number;
  label: string;
  value: string;
}

interface PairedRow {
  kind: "paired";
  order: number;
  label: string;
  entries: { code: string; poa: string | null; position: number }[];
}

type Row = SimpleRow | PairedRow;

const isNonEmpty = (value: unknown): value is string | number | boolean =>
  value !== null && value !== undefined && value !== "";

const readField = (claim: Claim, key: string): unknown =>
  (claim as unknown as Record<string, unknown>)[key];

const collectRows = (claim: Claim): Map<FieldGroup, Row[]> => {
  const rowsByGroup = new Map<FieldGroup, Row[]>();
  const pushRow = (group: FieldGroup, row: Row) => {
    const existing = rowsByGroup.get(group) ?? [];
    existing.push(row);
    rowsByGroup.set(group, existing);
  };

  for (const [key, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config) continue;
    const raw = readField(claim, key);
    if (!isNonEmpty(raw)) continue;
    const value = config.formatter ? config.formatter(raw) : String(raw);
    if (value === "") continue;
    pushRow(config.group, {
      kind: "simple",
      order: config.order,
      label: config.label,
      value,
    });
  }

  for (const derived of DERIVED_REGISTRY) {
    const value = derived.compute(claim);
    if (!value) continue;
    pushRow(derived.group, {
      kind: "simple",
      order: derived.order,
      label: derived.label,
      value,
    });
  }

  for (const agg of AGGREGATE_REGISTRY) {
    if (agg.aggregate === "paired") {
      const entries: PairedRow["entries"] = [];
      const len = Math.min(agg.codeKeys.length, agg.poaKeys.length);
      for (let i = 0; i < len; i++) {
        const codeKey = agg.codeKeys[i];
        const poaKey = agg.poaKeys[i];
        if (!codeKey || !poaKey) continue;
        const codeRaw = readField(claim, codeKey);
        if (!isNonEmpty(codeRaw)) continue;
        const poaRaw = readField(claim, poaKey);
        entries.push({
          code: String(codeRaw),
          poa: isNonEmpty(poaRaw) ? String(poaRaw) : null,
          position: i,
        });
      }
      if (entries.length === 0) continue;
      pushRow(agg.group, {
        kind: "paired",
        order: agg.order,
        label: agg.label,
        entries,
      });
    } else {
      const entries: PairedRow["entries"] = [];
      for (const [position, key] of agg.keys.entries()) {
        const raw = readField(claim, key);
        if (isNonEmpty(raw))
          entries.push({ code: String(raw), poa: null, position });
      }
      if (entries.length === 0) continue;
      pushRow(agg.group, {
        kind: "paired",
        order: agg.order,
        label: agg.label,
        entries,
      });
    }
  }

  for (const rows of rowsByGroup.values()) {
    rows.sort((a, b) => a.order - b.order);
  }

  return rowsByGroup;
};

const FieldRow = ({ label, value }: { label: string; value: string }) => (
  <div className="af-border-ds-interface-border/50 af-flex af-items-baseline af-justify-between af-gap-4 af-border-b af-py-1.5 last:af-border-none">
    <span className="af-text-sm af-font-medium af-text-interface-foreground/70">
      {label}
    </span>
    <span className="af-text-sm af-text-black">{value}</span>
  </div>
);

const PairedList = ({
  label,
  entries,
}: {
  label: string;
  entries: PairedRow["entries"];
}) => (
  <div className="af-border-ds-interface-border/50 af-flex af-flex-col af-gap-1 af-border-b af-py-1.5 last:af-border-none">
    <span className="af-text-sm af-font-medium af-text-interface-foreground/70">
      {label}
    </span>
    <ol className="af-flex af-flex-col af-gap-0.5">
      {entries.map((entry, idx) => (
        <li
          key={`${entry.code}-${idx}`}
          className="af-flex af-items-baseline af-gap-2 af-text-sm af-text-black"
        >
          <span className="af-min-w-[4.5rem] af-text-interface-foreground/60">
            {entry.position === 0 ? "Primary" : `# ${entry.position}`}
          </span>
          <span>{entry.code}</span>
          {entry.poa ? (
            <span className="af-text-interface-foreground/60">
              (POA: {entry.poa})
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  </div>
);

const renderRow = (row: Row) => {
  if (row.kind === "paired") {
    return (
      <PairedList
        key={`${row.label}-paired`}
        label={row.label}
        entries={row.entries}
      />
    );
  }
  return <FieldRow key={row.label} label={row.label} value={row.value} />;
};

export function ClaimDataCard({ claim }: ClaimDataCardProps) {
  const rowsByGroup = claim ? collectRows(claim) : null;
  const hasAnyRows = rowsByGroup
    ? Array.from(rowsByGroup.values()).some((rows) => rows.length > 0)
    : false;

  return (
    <div className="af-flex af-max-h-full af-flex-col af-gap-2 af-p-4">
      <div className="af-flex af-h-[36px] af-items-center">
        <h2 className="af-font-medium">Claim Data</h2>
      </div>
      <div className="af-border-ds-interface-border af-flex af-flex-1 af-flex-col af-gap-4 af-overflow-auto af-rounded-2xl af-border af-px-6 af-py-5">
        {rowsByGroup && hasAnyRows ? (
          GROUP_ORDER.map((group) => {
            const rows = rowsByGroup.get(group);
            if (!rows || rows.length === 0) return null;
            return (
              <section
                key={group}
                className="af-flex af-flex-col af-gap-1"
                aria-label={GROUP_LABELS[group]}
              >
                <h3 className="af-text-xs af-font-semibold af-uppercase af-tracking-wide af-text-interface-foreground/60">
                  {GROUP_LABELS[group]}
                </h3>
                <div className="af-flex af-flex-col">{rows.map(renderRow)}</div>
              </section>
            );
          })
        ) : (
          <p className="af-text-sm af-text-interface-foreground/70">
            No claim is linked to this case yet.
          </p>
        )}
      </div>
    </div>
  );
}
